import React, { Fragment, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { getBreadcrumbsStorage, getUrlParameter } from './helpers';

export const BreadCrumbSkeleton = ({ type }) => {
  let defaultClass = 'bread-crumb_skeleton';
  if (type) {
    defaultClass = `${defaultClass} ${type}`;
  }
  return <div className={defaultClass} />;
};

BreadCrumbSkeleton.propTypes = {
  type: PropTypes.string,
};

const CrumbSlash = ({ className }) => {
  const addedClass = className || '';
  return (
    <span className={`bread-crumbs-slash ${addedClass}`}>
      &nbsp;&nbsp;&nbsp;/&nbsp;&nbsp;&nbsp;
    </span>
  );
};

export const BreadCrumbs = (props) => {
  const { productData, className, crumbKey, fromChecklistQueryParam } = props;

  // this is a temporary fix: if url param has checklist, then render Checklist breadcrumb
  const isFromChecklist = typeof window !== 'undefined' ? getUrlParameter('fromChecklist') : fromChecklistQueryParam;

  const [breadcrumbsParams, setBreadcrumbsParams] = useState({});
  useEffect(() => {
    let params = getBreadcrumbsStorage(crumbKey);
    if (!params) {
      params = {};
    }
    setBreadcrumbsParams(params);
  }, []);

  if (isFromChecklist) {
    const searchLink = (
      <a href={`/registry/checklist/${isFromChecklist}?sentFromPDP=true`}>Checklist</a>
    );
    return (
      <div className={`bread-crumbs-pdp ${className}`}>
        {searchLink}
        <span className="bread-crumbs-product-name">
          <CrumbSlash />
          {productData.name}
        </span>
      </div>
    );
  }

  if (breadcrumbsParams.ref_type === 'SEARCH') {
    const searchLink = (
      <a
        href={`/search/gifts?q=${breadcrumbsParams.search_term}`}
      >{`Back to search results for '${breadcrumbsParams.search_term}'`}</a>
    );
    return (
      <div className={`bread-crumbs-pdp ${className}`}>
        {searchLink}
        <span className="bread-crumbs-product-name">
          <CrumbSlash />
          {productData.name}
        </span>
      </div>
    );
  }
  if (breadcrumbsParams.ref_type === 'COLLECTION') {
    const collectionLink = (
      <a href={`/shop/collection/${breadcrumbsParams.ref_key}`}>{breadcrumbsParams.ref_label}</a>
    );
    return (
      <div className={`bread-crumbs-pdp ${className}`}>
        {collectionLink}
        <span>
          <CrumbSlash />
          <span className="bread-crumbs-product-name">{productData.name}</span>
        </span>
      </div>
    );
  }
  if (breadcrumbsParams.ref_type === 'ADD_GIFTS') {
    return (
      <div className={`bread-crumbs-pdp ${className}`}>
        <a href="/registry/add-gifts">Add gifts</a>
        <span>
          <CrumbSlash />
          <span className="bread-crumbs-product-name">{productData.name}</span>
        </span>
      </div>
    );
  }
  if (breadcrumbsParams.ref_type === 'POST_WEDDING_SHOP') {
    return (
      <div className={`bread-crumbs-pdp ${className}`}>
        <a href="/registry/shop">Shop</a>
        <span>
          <CrumbSlash />
          <span className="bread-crumbs-product-name">{productData.name}</span>
        </span>
      </div>
    );
  }
  if (breadcrumbsParams.ref_type === 'FEATURED') {
    const registriesUrl = '/shop/registries-we-love';
    const couplesUrl = `${registriesUrl}/${breadcrumbsParams.ref_key}`;
    return (
      <div className={`bread-crumbs-pdp ${className}`}>
        <a href={registriesUrl}>Registries We Love</a>
        <CrumbSlash />
        <a href={couplesUrl}>{breadcrumbsParams.ref_label}</a>
        <CrumbSlash />
        <span className="bread-crumbs-product-name">{productData.name}</span>
      </div>
    );
  }
  if (breadcrumbsParams.ref_type === 'PARTNER_BOUTIQUE') {
    const partnerLink = (
      <a href={`/shop/partner-boutique/${breadcrumbsParams.ref_key}`}>
        {breadcrumbsParams.ref_label}
      </a>
    );
    return (
      <div className={`bread-crumbs-pdp ${className}`}>
        {partnerLink}
        <span>
          <CrumbSlash />
          <span className="bread-crumbs-product-name">{productData.name}</span>
        </span>
      </div>
    );
  }
  if (breadcrumbsParams.ref_type === 'BRAND') {
    const partnerLink = (
      <a href={`/shop/brand/${breadcrumbsParams.ref_key}`}>{breadcrumbsParams.ref_label}</a>
    );
    return (
      <div className={`bread-crumbs-pdp ${className}`}>
        {partnerLink}
        <span>
          <CrumbSlash />
          <span className="bread-crumbs-product-name">{productData.name}</span>
        </span>
      </div>
    );
  }
  if (breadcrumbsParams.ref_type === 'QUICK_ADD_PACKS') {
    return (
      <div className={`bread-crumbs-pdp ${className}`}>
        <a href="/registry/quick-add-packs">Quick-Add Packs</a>
        <CrumbSlash />
        <a href={`/registry/quick-add-packs/${breadcrumbsParams.ref_key}`}>
          {breadcrumbsParams.ref_label}
        </a>
        <CrumbSlash className="no-mobile" />
        <span className="qap-no-mobile">{breadcrumbsParams.ref_name}</span>
      </div>
    );
  }
  // default categoryChain from API will be used if custom chain is not present
  let categoryChain = productData.category_chain;
  if (breadcrumbsParams.ref_type === 'CATEGORY_CHAIN') {
    const categoryKeys = Object.keys(breadcrumbsParams);
    categoryChain = [];
    categoryKeys.forEach((key) => {
      if (key !== 'ref_type') {
        // grab and use the index here to maintain the original order from the category chain in PLP
        // example of query param: &ref_key_0=bridal-boutique|Bridal%20Boutique
        const index = key.replace('ref_key_', '');
        const categoryVals = breadcrumbsParams[key].split('|');
        categoryChain[index] = { key: categoryVals[0], name: categoryVals[1] };
      }
    });
  }
  if (!categoryChain || categoryChain.length === 0) return null;
  return (
    <div className={`bread-crumbs-pdp ${className}`}>
      {categoryChain.map((category, i) => (
        <Fragment key={i}>
          <a href={`/shop/category/${category.key}`}>{category.name}</a>
          {i !== categoryChain.length - 1 ? <CrumbSlash /> : <CrumbSlash className="no-mobile" />}
          {i === categoryChain.length - 1 && (
            <span className="bread-crumbs-product-name">{productData.name}</span>
          )}
        </Fragment>
      ))}
    </div>
  );
};

BreadCrumbs.propTypes = {
  productData: PropTypes.shape({}),
  className: PropTypes.string,
  crumbKey: PropTypes.string,
  fromChecklistQueryParam: PropTypes.string,
};

CrumbSlash.propTypes = {
  className: PropTypes.string,
};
